import React from 'react';
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, Text } from '@chakra-ui/react';
import CustomBadge from '../../CustomBadge/CustomBadge';
import IconComponent from '../../IconComponent/IconComponent';
import CustomChakraButton from '../../CustomChakraButton/CustomChakraButton';
import GetButtonMessage from '../../../utils/GetButtonMessage';
import { ParentAbsenceInterfaceFormMoth } from '../../../shared/type/absence.type';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';

type AbsencesTableProps = {
  isLoading: boolean;
  absences?: ParentAbsenceInterfaceFormMoth[];
  onAbsenceToggle: (stakeId: number, presence: boolean) => void;
};

export const ParentAbsencesTable = ({
  isLoading,
  absences,
  onAbsenceToggle
}: AbsencesTableProps) => {
  const { t } = useSafeTranslation();

  return (
    <Table className="w-full">
      <Thead className="text-xs bg-white p-1 mb-4">
        <Tr>
          <Th className="w-[70px]"></Th>

          <Th textTransform="capitalize">{t('parent_absences.day')}</Th>
          <Th textTransform="capitalize">{t('parent_absences.meal_names')}</Th>
          <Th textTransform="capitalize">{t('parent_absences.meal_categories')}</Th>
          <Th textTransform="capitalize">{t('parent_absences.pickup_status')}</Th>
        </Tr>
      </Thead>
      <Tbody className="text-sm font-medium text-grayLight-900">
        {isLoading ? (
          <Tr>
            <Td colSpan={5}>
              <Center>
                <Spinner size="xl" />
              </Center>
            </Td>
          </Tr>
        ) : absences && absences.length === 0 ? (
          <Tr>
            <Td colSpan={5}>
              <Center>
                <Text>{t('parent_absences.empty')}</Text>
              </Center>
            </Td>
          </Tr>
        ) : (
          absences?.map((absence) => {
            const date = new Date(absence.when);
            const dayName = absence.dayOfTheWeek;
            const formattedDate = `${String(date.getDate()).padStart(2, '0')}.${String(
              date.getMonth() + 1
            ).padStart(2, '0')}`;

            return absence.stakesAndCourses.flatMap((stake, stakeIndex) =>
              stake.takenCourses.map((course, courseIndex) => {
                const isFirstDateCell = stakeIndex === 0 && courseIndex === 0;
                const isFirstStakeRow = courseIndex === 0;
                const rowSpanForStake = stake.takenCourses.length;

                return (
                  <Tr key={`${absence.when}-${stakeIndex}-${courseIndex}`}>
                    {isFirstStakeRow && (
                      <Td rowSpan={rowSpanForStake} className={!stake.presence ? 'bg-red-50' : ''}>
                        <div className="flex flex-col items-start gap-1">
                          <CustomChakraButton
                            buttonProps={{
                              onClick: () => onAbsenceToggle(stake.boughtStakeId, stake.presence),
                              disabled: !absence.reportingAbsencePossible
                            }}
                            size="sm"
                            iconPosition={stake.presence ? 'left' : 'right'}
                            iconColor={stake.presence ? '#B42318' : '#292524'}
                            icon={stake.presence ? 'xClose' : 'rollBack'}
                            hierarchy={
                              !absence.reportingAbsencePossible
                                ? 'destructiveSecondary'
                                : stake.presence
                                  ? 'destructiveSecondary'
                                  : 'secondaryColor'
                            }>
                            {stake.presence
                              ? t('parent_absences.revoke_absence')
                              : t('parent_absences.restore_absence')}
                          </CustomChakraButton>
                          <GetButtonMessage
                            absencePossible={absence.reportingAbsencePossible}
                            presence={stake.presence}
                          />
                        </div>
                      </Td>
                    )}

                    {isFirstDateCell && (
                      <Td
                        rowSpan={absence.stakesAndCourses.reduce(
                          (acc, s) => acc + s.takenCourses.length,
                          0
                        )}>
                        <div className="flex flex-col">
                          <p className="text-grayLight-900 font-medium text-sm">{dayName}</p>
                          <p className="text-grayLight-600 font-normal text-sm">{formattedDate}</p>
                        </div>
                      </Td>
                    )}

                    {isFirstStakeRow && (
                      <Td rowSpan={rowSpanForStake} className={!stake.presence ? 'bg-red-50' : ''}>
                        {stake.presence ? (
                          stake.stakeName
                        ) : (
                          <p className="text-red-700 font-semibold text-sm">
                            [{t('parent_absences.stake_cancelled')}] {stake.stakeName}
                          </p>
                        )}
                      </Td>
                    )}

                    <Td className={!stake.presence ? 'bg-red-50' : ''}>
                      {stake.presence ? course.courseName : ''}
                    </Td>

                    <Td className={!stake.presence ? 'bg-red-50' : ''}>
                      <CustomBadge size="sm" color={course.taken ? 'success' : 'gray'}>
                        <div className="flex items-center gap-spacing-xs">
                          {course.taken ? t('parent_absences.yes') : t('parent_absences.no')}
                          <IconComponent
                            iconName={course.taken ? 'check' : 'xClose'}
                            className="h-2"
                          />
                        </div>
                      </CustomBadge>
                    </Td>
                  </Tr>
                );
              })
            );
          })
        )}
      </Tbody>
    </Table>
  );
};
